/**
 * FAIRy Welcome Modal Component
 *
 * First-time user onboarding modal that explains FAIR Compass and journeys.
 * Shown once on first dashboard visit, with option to dismiss.
 *
 * @module client/components/FairyWelcomeModal
 * @version 1.0
 * @date 2025-10-27
 */

import React, { useState, useEffect } from 'react';
import { BRANDING } from '../config/branding';

interface FairyWelcomeModalProps {
  isFirstTime: boolean;
  onStartJourney: () => void;
  onDismiss: () => void;
}

const STORAGE_KEY = 'fairy_welcome_dismissed';

export const FairyWelcomeModal: React.FC<FairyWelcomeModalProps> = ({
  isFirstTime,
  onStartJourney,
  onDismiss
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already dismissed the modal
    const dismissed = localStorage.getItem(STORAGE_KEY);

    if (isFirstTime && !dismissed) {
      // Show modal after a short delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isFirstTime]);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem(STORAGE_KEY, 'true');
    onDismiss();
  };

  const handleStartJourney = () => {
    setIsVisible(false);
    localStorage.setItem(STORAGE_KEY, 'true');
    onStartJourney();
  };

  if (!isVisible) return null;

  return (
    <div className="fairy-welcome-overlay" onClick={handleDismiss}>
      <div className="fairy-welcome-modal animate-slide-in-up" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-icon">✨</div>
          <h2>{`Welcome to ${BRANDING.productName}!`}</h2>
          <button
            className="modal-close-btn"
            onClick={handleDismiss}
            aria-label="Close welcome modal"
          >
            ×
          </button>
        </div>

        {/* Introduction */}
        <div className="modal-body">
          <p className="modal-intro">
            I'm <strong>FAIRy</strong>, your AI copilot for FAIR data improvement.
            Let me show you how to get started!
          </p>

          {/* Steps */}
          <div className="welcome-steps">
            <div className="step">
              <div className="step-icon">1️⃣</div>
              <div className="step-content">
                <div className="step-title">Create a Journey</div>
                <div className="step-description">
                  Each journey represents a dataset or project you want to make FAIR
                  (Findable, Accessible, Interoperable, Reusable).
                </div>
              </div>
            </div>

            <div className="step">
              <div className="step-icon">2️⃣</div>
              <div className="step-content">
                <div className="step-title">Complete Stations</div>
                <div className="step-description">
                  Upload documents, run checks, and get personalized recommendations
                  at each station along your journey.
                </div>
              </div>
            </div>

            <div className="step">
              <div className="step-icon">3️⃣</div>
              <div className="step-content">
                <div className="step-title">Improve Your FAIR Score</div>
                <div className="step-description">
                  Track progress, implement recommendations, and export Journey Maps
                  to share your FAIR achievements.
                </div>
              </div>
            </div>
          </div>

          {/* What FAIRy Can Do */}
          <div className="fairy-capabilities">
            <h4>I can help you:</h4>
            <ul>
              <li>✅ Create your first FAIR improvement journey</li>
              <li>✅ Understand FAIR principles in plain language</li>
              <li>✅ Guide you through each station</li>
              <li>✅ Explain recommendations and suggest next steps</li>
              <li>✅ Track patterns across all your journeys</li>
            </ul>
          </div>
        </div>

        {/* Actions */}
        <div className="modal-footer">
          <button
            className="btn btn-primary btn-lg"
            onClick={handleStartJourney}
          >
            Create My First Journey with FAIRy
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleDismiss}
          >
            Explore on My Own
          </button>
        </div>

        {/* Footer Note */}
        <div className="modal-note">
          <small>You can always open FAIRy by clicking the ✨ button</small>
        </div>
      </div>
    </div>
  );
};

export default FairyWelcomeModal;
